/**
 * API Service
 * Axios-based wrapper for all backend API calls
 */

import axios, { AxiosError } from 'axios';
import {
  ApiResponse,
  PaginatedResponse,
  Run,
  RunCostEstimate,
  Participant,
  RunLogEntry,
  SystemStatus,
  VMControlResponse,
  DashboardStats,
  PasswordResetResult,
  AuditLogEntry,
  ReleaseNotesData,
  AzureResourceListingResponse,
  ParticipantUpdateResponse,
  ParticipantLockError,
  ProvisionResponse,
  ProvisionLockError,
  ResourceMetadata,
  ResourceMetadataInput,
  DriftListingResponse,
  PersistentResourcesListingResponse,
  RunDiagnostics,
  WelcomePayload,
  ParticipantLink,
  CollectedProjectFile,
  FastCleanupToggleResult,
  KeepRunningToggleResult,
  RdpAccessToggleResult,
  ClientIpResult,
  KeepRunningStatusResult,
  RunClassification,
  ClassificationSetResult,
  RunRetrospective,
  RunRetrospectiveInput,
} from './types';
import { reportApiError } from './errorReporter';

// NC-1383: default export only. Ten sibling services import it that way; the
// extra named export gave one caller a second path to the same singleton.
const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Global error callback — set by ErrorProvider via setApiErrorHandler().
 * When set, non-401 errors are forwarded to the global error toast.
 */
let globalErrorHandler: ((message: string, retryFn?: () => void) => void) | null = null;

export function setApiErrorHandler(handler: typeof globalErrorHandler) {
  globalErrorHandler = handler;
}

// NC-529: dashboard polls /api/stats + /api/runs every 30s, so a sustained
// transient 5xx surfaced one toast per cycle ("kommt immer wieder die Meldung
// Serverfehler"). Dedupe identical toast messages within a sliding window so
// repeated background failures show at most one toast per minute; mutations
// and distinct error messages still fire normally.
const TOAST_DEDUPE_WINDOW_MS = 60_000;
let lastToastMessage: string | null = null;
let lastToastAt = 0;

/** Test-only — reset the dedupe state between cases. */
export function resetApiErrorToastDedupeForTests() {
  lastToastMessage = null;
  lastToastAt = 0;
}

/**
 * Extract a user-friendly German error message from an Axios error.
 *
 * NC-531: prefers the BE-provided `error` body for 5xx as well — previously the
 * 500 branch always returned a generic "Serverfehler" string, which hid actionable
 * messages like the password-endpoint's "Passwort konnte nicht aus KeyVault
 * geladen werden. … Admin kontaktieren". The generic German wording stays as the
 * fallback when the response has no body (transient gateway 5xx, etc.).
 *
 * Exported so page-level catch blocks (RunDetailsPage password reveal, ...) can
 * mirror the interceptor's mapping instead of falling back to the raw axios
 * "Request failed with status code N" string.
 */
export function extractErrorMessage(error: unknown): string {
  const axiosErr = error as AxiosError<ApiResponse<unknown>>;
  if (!axiosErr?.response) {
    if (axiosErr?.code === 'ECONNABORTED') return 'Zeitüberschreitung — der Server antwortet nicht.';
    if (axiosErr?.code === 'ERR_NETWORK') return 'Netzwerkfehler — bitte Verbindung prüfen.';
    if (error instanceof Error) return error.message;
    return 'Netzwerkfehler — der Server ist nicht erreichbar.';
  }

  const status = axiosErr.response.status;
  const serverMsg = axiosErr.response.data?.error;

  if (status === 403) return 'Keine Berechtigung für diese Aktion.';
  if (status === 404) return serverMsg || 'Die angeforderte Ressource wurde nicht gefunden.';
  if (status >= 500) return serverMsg || 'Serverfehler — bitte später erneut versuchen.';

  return serverMsg || 'Ein unerwarteter Fehler ist aufgetreten.';
}

// Request interceptor for logging (dev only)
apiClient.interceptors.request.use((config) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
  }
  return config;
});

// NC-650: error codes the BE explicitly marks as "expected, recoverable" —
// the operator's snackbar already points them at the recovery path (e.g.
// "Passwort zurücksetzen"), so auto-filing these into the /events portal
// backlog only generates noise. Codes are sourced from the BE response's
// `details.errorCode` (e.g. errorHandler.ts ApiResponse `details` payload
// in web-portal/api/**); add a new code here when its 4xx response is
// purely user-actionable. The error toast still fires — only the
// silent /api/errors capture is skipped.
const EXPECTED_ACTIONABLE_ERROR_CODES = new Set<string>([
  'PASSWORD_NOT_IN_KEYVAULT',
  // NC-1264: the tutor picked a workload that was deleted or deactivated
  // between loading the list and submitting. Both are stale-selection cases
  // with a clear toast and an obvious recovery (pick another workload) — the
  // two that actually produced hand-closed noise issues (#826, #827).
  'WORKLOAD_NOT_FOUND',
  'WORKLOAD_INACTIVE',
  // NC-1274: the welcome-card sibling of PASSWORD_NOT_IN_KEYVAULT. The BE has
  // classified it as expected+actionable since the endpoint existed — its
  // message literally names the recovery ("Passwort zuruecksetzen") — but the
  // code never made it into this list, so every occurrence was captured and two
  // got filed as bug issues three minutes apart (#1274, #1276).
  'WELCOME_PASSWORD_NOT_IN_KEYVAULT',
  // NC-1647: runs-create refuses a run that does not fit the vCPU quota. The
  // toast names the numbers and the fix (fewer participants / more quota);
  // QUOTA_CHECK_UNAVAILABLE (503) stays captured, that one is a real defect.
  'QUOTA_INSUFFICIENT',
]);

function isExpectedActionableError(error: AxiosError<ApiResponse<any>>): boolean {
  const errorCode = (error.response?.data as { details?: { errorCode?: string } } | undefined)
    ?.details?.errorCode;
  return typeof errorCode === 'string' && EXPECTED_ACTIONABLE_ERROR_CODES.has(errorCode);
}

// NC-1148/NC-1149/NC-1150: SWA Managed Functions scale to zero when idle, so the
// first request after an idle window (or a platform host-recycle) can surface a
// transient 5xx / dropped connection at the SWA edge *before the Function even
// runs* — App Insights shows 0 server-side 5xx across /stats, /rooms and /runs,
// only steady 200s, yet the browser captured one-off 500/503s that recovered ~1s
// later. Retry idempotent GETs a couple of times on such transient failures so a
// cold start no longer fires the "Serverfehler" toast for a request that then
// succeeds. A *real* BE 5xx always carries a `{success:false, error}` body
// (errorHandler.ts) — those are NOT retried and toast immediately (preserving
// NC-531/NC-650). Persistent transient 5xx exhaust the retries and still toast.
const RETRYABLE_METHODS = new Set(['get', 'head']);
const RETRYABLE_STATUS = new Set([500, 502, 503, 504]);
const MAX_RETRIES = 2;

let retryBackoff = (attempt: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, attempt === 1 ? 300 : 800));

/** Test-only — make the retry backoff instant so specs don't wait on timers. */
export function setRetryBackoffForTests(fn: (attempt: number) => Promise<void>) {
  retryBackoff = fn;
}

function isTransientRetryable(error: AxiosError<ApiResponse<any>>): boolean {
  const method = (error.config?.method ?? 'get').toLowerCase();
  if (!error.config || !RETRYABLE_METHODS.has(method)) return false;

  // No response at all = network blip / dropped edge connection. Retry, except
  // a client-side timeout (ECONNABORTED) — retrying a slow call only compounds
  // the latency the user is already waiting through.
  if (!error.response) return error.code === 'ERR_NETWORK';

  if (!RETRYABLE_STATUS.has(error.response.status)) return false;
  // A body-carried `error` marks a deterministic BE failure (handleApiError),
  // not a transient edge blip — surface it immediately instead of retrying.
  return !error.response.data?.error;
}

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<any>>) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Transient-5xx / network-blip retry for idempotent GETs, BEFORE the toast
    // and /events capture, so a recovered cold start is invisible to the user.
    if (isTransientRetryable(error)) {
      const cfg = error.config as (NonNullable<typeof error.config> & { __retryCount?: number });
      const attempt = (cfg.__retryCount ?? 0) + 1;
      if (attempt <= MAX_RETRIES) {
        cfg.__retryCount = attempt;
        await retryBackoff(attempt);
        return apiClient.request(cfg);
      }
    }

    // Dispatch to global error toast (non-401 errors); see NC-529 dedupe note.
    if (globalErrorHandler) {
      const message = extractErrorMessage(error);
      const now = Date.now();
      const isDuplicate =
        message === lastToastMessage && now - lastToastAt < TOAST_DEDUPE_WINDOW_MS;
      if (!isDuplicate) {
        lastToastMessage = message;
        lastToastAt = now;
        const retryConfig = error.config;
        const retryFn = retryConfig
          ? () => { apiClient.request(retryConfig); }
          : undefined;
        globalErrorHandler(message, retryFn);
      }
    }

    // P2: silent capture for the admin /events view. The reporter itself
    // skips its own endpoint and throttles, so this is safe to call on
    // every non-401 failure.
    if (error.config && !isExpectedActionableError(error)) {
      const requestUrl = error.config.url ?? '';
      reportApiError(
        {
          status: error.response?.status ?? 0,
          method: (error.config.method ?? 'GET').toUpperCase(),
          requestUrl,
          requestId: error.response?.headers?.['x-request-id'] as string | undefined,
          serverError: error.response?.data?.error,
        },
        extractErrorMessage(error),
      );
    }

    return Promise.reject(error);
  }
);

/**
 * NC-921 / NC-922: the runs container holds sparse documents (status=test
 * artifacts, legacy runs) that may lack the `runId` field while always carrying
 * the Cosmos document `id`. By construction `id === pk === runId` for every run
 * (see api/runs-create/index.ts and api/shared/cosmosClient.ts, which reads runs
 * via `readItem('runs', runId, runId)`). The FE types `Run.runId` as a required
 * string, so a missing field surfaced as `/runs/undefined` navigation (#922) and
 * `/runs-cost/undefined` fetches (#921) on the dashboard. Backfill the identifier
 * from whichever field is present so every list consumer (DashboardPage,
 * RunsPage, RunsCostSummaryCard) receives a usable run id. Sibling of the
 * date-field hardening in NC-794/NC-896 (utils/dateFormat.ts).
 */
export function normalizeRunIdentity(run: Run): Run {
  const identity = run.runId ?? run.id ?? run.pk;
  return { ...run, runId: identity, id: run.id ?? identity, pk: run.pk ?? identity };
}

/**
 * Runs API
 */
export const runsApi = {
  /**
   * Create a new training run
   */
  async create(data: {
    workloadId: string;
    // NC-867 T4: mandatory cost-center — the run is chargeable and 110-infra-run.ps1
    // refuses to provision without a valid, active Kostenstelle.
    costCenter: string;
    course: string;
    expireOn: string;
    scheduledStart?: string;
    roomId?: string;
    // NC-1173 (#1164-S2b): optional public-RDP access. Sent only when the operator
    // ticks the box; the API validates + normalizes (empty CIDR → company egress).
    access?: { publicRdp: boolean; allowedSourceCidrs: string[] };
    // NC-1168 (Slice 3): opt-in "Testlauf: direkt nach Ende aufräumen". Sent only
    // when ticked; without it the run keeps today's retention behaviour.
    fastCleanup?: boolean;
    // NC-1567: admin-only opt-out from the 19:00 evening shutdown.
    keepRunning?: boolean;
    // NC-1074: externalEmail/company are optional contact metadata captured at creation.
    participants: Array<{ firstName: string; lastName: string; externalEmail?: string; company?: string }>;
  }): Promise<Run> {
    const response = await apiClient.post<ApiResponse<Run>>('/runs', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create run');
    }
    return response.data.data;
  },

  /**
   * NC-1168 (Slice 3, retro path): flip the fast-cleanup opt-in on an EXISTING
   * run — `runs-create` only covers the opt-in at creation time.
   *
   * operator+; 404 unknown run, 409 when the run is already terminal
   * (deleted/expired — nothing left to clean up). `cleanupImminent` in the
   * response means end + grace has already passed, so the very next sweep tears
   * the run down; the caller must warn rather than let that surprise the tutor.
   */
  async setFastCleanup(runId: string, enabled: boolean): Promise<FastCleanupToggleResult> {
    const response = await apiClient.post<ApiResponse<FastCleanupToggleResult>>(
      `/runs/${encodeURIComponent(runId)}/fast-cleanup`,
      { enabled },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to toggle fast cleanup');
    }
    return response.data.data;
  },

  /**
   * NC-1567: set/remove the KeepRunning tag on all VMs of the run (admin only).
   *
   * 409 terminal or still-provisioning run. A (partial) compute failure answers
   * 207/502 with success=false — the error names the failed VMs and the run doc
   * keeps its old value, so the caller just re-fetches and lets the admin retry.
   */
  async setKeepRunning(runId: string, enabled: boolean): Promise<KeepRunningToggleResult> {
    const response = await apiClient.post<ApiResponse<KeepRunningToggleResult>>(
      `/runs/${encodeURIComponent(runId)}/keep-running`,
      { enabled },
    );
    // 207 (partial) resolves as a 2xx with success=false → surface its error.
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to toggle keep-running');
    }
    return response.data.data;
  },

  /**
   * NC-1579: switch public RDP on an ACTIVE run and/or replace its source-IP
   * whitelist (operator+). Re-sending `enabled: true` with a different list
   * updates the NSG rule in place. 202 = dispatched (applied in ~1-2 min by
   * run-rdp-toggle.yml), 200 + noop = nothing to change. 400 invalid/too broad
   * CIDR, 409 run not active or a toggle still in flight.
   */
  async setRdpAccess(runId: string, enabled: boolean, allowedSourceCidrs: string[] = []): Promise<RdpAccessToggleResult> {
    const response = await apiClient.post<ApiResponse<RdpAccessToggleResult>>(
      `/runs/${encodeURIComponent(runId)}/rdp`,
      enabled ? { enabled, allowedSourceCidrs } : { enabled },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to change RDP access');
    }
    return response.data.data;
  },

  /**
   * NC-1580: the run's REAL evening-shutdown opt-out, read from the VM tags
   * (operator+). The switch renders `effective`; `drift` flags a run doc that
   * disagrees with what the 19:00 runbook will actually do.
   */
  async getKeepRunningStatus(runId: string): Promise<KeepRunningStatusResult> {
    const response = await apiClient.get<ApiResponse<KeepRunningStatusResult>>(
      `/runs/${encodeURIComponent(runId)}/keep-running/status`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to read keep-running status');
    }
    return response.data.data;
  },

  /**
   * NC-1288 (retro path): state the Test-/Produktiv-Kennzeichnung on an EXISTING
   * run. `runs-create` only covers the label at creation time, which misses the
   * common case — a run is usually only recognised as a test run once it is
   * already up, and every legacy run predates the field entirely.
   *
   * operator+; 404 unknown run, 400 on an unknown value. Unlike
   * `setFastCleanup` this does NOT 409 on a terminal run: classification is
   * reporting metadata, and retro-labelling a finished E2E run is precisely how
   * the statistics get cleaned up.
   */
  async setClassification(
    runId: string,
    classification: RunClassification,
  ): Promise<ClassificationSetResult> {
    const response = await apiClient.post<ApiResponse<ClassificationSetResult>>(
      `/runs/${encodeURIComponent(runId)}/classification`,
      { classification },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to set run classification');
    }
    return response.data.data;
  },

  /**
   * NC-1281 S3: file the Run-Rueckblick for a finished run.
   *
   * Upsert semantics — the document id is `retro-<runId>`, so a second call
   * REPLACES the first. The caller must therefore send the complete record, not
   * just the field that changed; `RunRetrospectiveDialog` pre-loads the existing
   * one for exactly that reason.
   *
   * operator+; 404 unknown run, 400 when the run has no workload reference (a
   * retrospective is filed against the workload's partition, so a run without
   * one has nowhere to go).
   */
  async saveRetrospective(
    runId: string,
    input: RunRetrospectiveInput,
  ): Promise<RunRetrospective> {
    const response = await apiClient.post<ApiResponse<RunRetrospective>>(
      `/runs/${encodeURIComponent(runId)}/retrospective`,
      input,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Rueckblick konnte nicht gespeichert werden');
    }
    return response.data.data;
  },

  /**
   * Get list of runs with optional filters
   */
  async list(params?: {
    workload?: string;
    status?: string;
    // NC-1077: 'active' (default) hides terminal runs; 'all' shows full history.
    view?: 'active' | 'all';
    dateFrom?: string;
    dateTo?: string;
    // NC-1288: server-side filter on the effective classification. Applied in
    // memory by runs-list (the rule is only partly persisted), so it composes
    // with the status/workload filters and keeps total/hasMore honest.
    classification?: RunClassification;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<Run>> {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Run>>>('/runs', { params });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch runs');
    }
    const data = response.data.data;
    // NC-921/NC-922: guarantee a usable runId on every list item so dashboard
    // navigation and cost-fetch never receive `undefined` from sparse docs.
    return { ...data, items: data.items.map(normalizeRunIdentity) };
  },

  /**
   * Get run details by ID
   */
  async getById(runId: string): Promise<Run> {
    const response = await apiClient.get<ApiResponse<Run>>(`/runs/${runId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch run details');
    }
    return response.data.data;
  },

  /**
   * NC-458: Live-state diagnostics for a run (RG / VMs / AD / KV). Read-only,
   * operator+. Each section reports its own status — the endpoint returns 200
   * even when a single source (e.g. DC unreachable) fails.
   */
  async diagnostics(runId: string): Promise<RunDiagnostics> {
    const response = await apiClient.get<ApiResponse<RunDiagnostics>>(
      `/runs/${runId}/diagnostics`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch run diagnostics');
    }
    return response.data.data;
  },

  /**
   * Extend run expiration date
   */
  async extend(runId: string, newExpireDate: string): Promise<Run> {
    const response = await apiClient.post<ApiResponse<Run>>(`/runs/${runId}/extend`, {
      newExpireDate,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to extend run');
    }
    return response.data.data;
  },

  /**
   * Start VMs for a run
   */
  async startVMs(runId: string): Promise<{ results: VMControlResponse[]; message?: string }> {
    const response = await apiClient.post<ApiResponse<VMControlResponse[]>>(
      `/runs/${runId}/vms/start`
    );

    // Handle both full success (200) and partial success (207)
    if (response.status === 207 || (response.data.success && response.data.data)) {
      return {
        results: response.data.data!,
        message: response.data.message,
      };
    }

    throw new Error(response.data.error || 'Failed to start VMs');
  },

  /**
   * Stop VMs for a run
   */
  async stopVMs(runId: string): Promise<{ results: VMControlResponse[]; message?: string }> {
    const response = await apiClient.post<ApiResponse<VMControlResponse[]>>(
      `/runs/${runId}/vms/stop`
    );

    // Handle both full success (200) and partial success (207)
    if (response.status === 207 || (response.data.success && response.data.data)) {
      return {
        results: response.data.data!,
        message: response.data.message,
      };
    }

    throw new Error(response.data.error || 'Failed to stop VMs');
  },

  /**
   * Restart VMs for a run
   */
  async restartVMs(runId: string): Promise<{ results: VMControlResponse[]; message?: string }> {
    const response = await apiClient.post<ApiResponse<VMControlResponse[]>>(
      `/runs/${runId}/vms/restart`
    );

    // Handle both full success (200) and partial success (207)
    if (response.status === 207 || (response.data.success && response.data.data)) {
      return {
        results: response.data.data!,
        message: response.data.message,
      };
    }

    throw new Error(response.data.error || 'Failed to restart VMs');
  },

  /**
   * NC-459 / NC-530: Soft-delete a run (admin-only).
   *
   * BE flips `status='deleted'`, dispatches `run-cleanup.yml`, and returns 202
   * with the GitHub Actions job URL so the FE can deep-link to the cleanup
   * progress. 409 from the BE means the run is already in the `deleted` state —
   * we surface a generic Error in that case so the caller's catch block can
   * show a snackbar; deeper resurfacing isn't worth a typed error here.
   */
  async deleteRun(runId: string): Promise<{ jobUrl: string | null; deletedAtUtc: string; message: string }> {
    const response = await apiClient.delete<ApiResponse<{ jobUrl: string | null; deletedAtUtc: string; message: string }>>(
      `/runs/${encodeURIComponent(runId)}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to delete run');
    }
    return response.data.data;
  },

  /**
   * NC-519: Add a participant to an existing run (operator+ only).
   *
   * Returns 202 + jobUrl on dispatch. On 409 (already-in-progress within
   * the 5-min lock window) throws a `ParticipantLockError` carrying
   * `triggeredAtUtc` so the UI can show "läuft bereits (vor Xs)".
   */
  async addParticipant(
    runId: string,
    data: { firstName: string; lastName: string }
  ): Promise<ParticipantUpdateResponse> {
    try {
      const response = await apiClient.post<ApiResponse<ParticipantUpdateResponse>>(
        `/runs/${runId}/participants`,
        data
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Failed to add participant');
      }
      return response.data.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const payload = err.response.data as ApiResponse<{ jobUrl?: string | null; triggeredAtUtc?: string }>;
        const lockErr = new Error(payload?.error || 'Update already in progress') as ParticipantLockError;
        lockErr.status = 409;
        lockErr.triggeredAtUtc = payload?.data?.triggeredAtUtc;
        lockErr.jobUrl = payload?.data?.jobUrl ?? null;
        throw lockErr;
      }
      throw err;
    }
  },

  /**
   * NC-519: Remove a participant from an existing run (operator+ only).
   * Same 202 + 409-lock contract as addParticipant.
   */
  async removeParticipant(runId: string, sam: string): Promise<ParticipantUpdateResponse> {
    try {
      const response = await apiClient.delete<ApiResponse<ParticipantUpdateResponse>>(
        `/runs/${runId}/participants/${encodeURIComponent(sam)}`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Failed to remove participant');
      }
      return response.data.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const payload = err.response.data as ApiResponse<{ jobUrl?: string | null; triggeredAtUtc?: string }>;
        const lockErr = new Error(payload?.error || 'Update already in progress') as ParticipantLockError;
        lockErr.status = 409;
        lockErr.triggeredAtUtc = payload?.data?.triggeredAtUtc;
        lockErr.jobUrl = payload?.data?.jobUrl ?? null;
        throw lockErr;
      }
      throw err;
    }
  },

  /**
   * NC-778 (D6): Trigger provisioning for a run in status 'provisioning'.
   *
   * BE (runs-provision) dispatches run-provision.yml and returns 202 with
   * jobUrl + provisioningStartedUtc. On 409 (30-min dispatch lock or wrong
   * run state) throws a `ProvisionLockError` carrying the lock metadata so
   * the UI can explain instead of crashing. Same contract as addParticipant.
   */
  async provision(runId: string): Promise<ProvisionResponse> {
    try {
      const response = await apiClient.post<ApiResponse<ProvisionResponse>>(
        `/runs/${encodeURIComponent(runId)}/provision`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Failed to start provisioning');
      }
      return response.data.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const payload = err.response.data as ApiResponse<{ jobUrl?: string | null; provisioningStartedUtc?: string }>;
        const lockErr = new Error(payload?.error || 'Provisioning already in progress') as ProvisionLockError;
        lockErr.status = 409;
        lockErr.provisioningStartedUtc = payload?.data?.provisioningStartedUtc;
        lockErr.jobUrl = payload?.data?.jobUrl ?? null;
        throw lockErr;
      }
      throw err;
    }
  },

  /**
   * NC-1101: rotate + fetch the participant magic links (operator+).
   * Every call INVALIDATES previously distributed links (BE persists only
   * hashes) — the UI warns before calling.
   */
  async participantLinks(runId: string): Promise<{ runId: string; links: ParticipantLink[] }> {
    const response = await apiClient.post<ApiResponse<{ runId: string; links: ParticipantLink[] }>>(
      `/runs/${encodeURIComponent(runId)}/participant-links`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Teilnehmer-Links konnten nicht erzeugt werden');
    }
    return response.data.data;
  },

  /**
   * NC-1101: trigger the Projektdateien collection (operator+, active runs).
   * 409 = 5-minute dispatch lock; surfaced with the BE message.
   */
  async collectProjectFiles(runId: string): Promise<{ runId: string; jobUrl: string | null }> {
    const response = await apiClient.post<ApiResponse<{ runId: string; jobUrl: string | null }>>(
      `/runs/${encodeURIComponent(runId)}/collect`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Einsammlung konnte nicht gestartet werden');
    }
    return response.data.data;
  },

  /**
   * NC-1101: list collected Projektdateien zips incl. tutor download SAS (operator+).
   */
  async projectFiles(runId: string): Promise<{ runId: string; files: CollectedProjectFile[] }> {
    const response = await apiClient.get<ApiResponse<{ runId: string; files: CollectedProjectFile[] }>>(
      `/runs/${encodeURIComponent(runId)}/project-files`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Projektdateien konnten nicht geladen werden');
    }
    return response.data.data;
  },

  /**
   * NC-1097/NC-1098: trigger ad-hoc redistribution of the training materials to
   * the run VMs ("Jetzt auf VMs verteilen"). operator+, active runs only. The BE
   * enforces a 5-min dispatch lock (409) and a not-active guard (400) — the UI
   * disables the button accordingly, but surfaces the BE message if it races.
   */
  async materialsSync(
    runId: string
  ): Promise<{ runId: string; jobUrl: string | null; requestedUtc: string }> {
    const response = await apiClient.post<
      ApiResponse<{ runId: string; jobUrl: string | null; requestedUtc: string }>
    >(`/runs/${encodeURIComponent(runId)}/materials-sync`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Verteilung konnte nicht gestartet werden');
    }
    return response.data.data;
  },

  /**
   * NC-1235 (V2): trigger the acceptance verification for a run ("Lauf prüfen").
   * operator+; the BE (runs-verify, #1255) answers 202 and dispatches
   * run-verify.yml. A 5-min dispatch lock yields 409 and a status gate rejects
   * only `planned`/`deleted` with 400 — the UI disables the button for both, but
   * surfaces the BE message if it races. Read-only: never rotates tokens.
   */
  async verify(
    runId: string
  ): Promise<{ runId: string; jobUrl: string | null; requestedUtc: string }> {
    const response = await apiClient.post<
      ApiResponse<{ runId: string; jobUrl: string | null; requestedUtc: string }>
    >(`/runs/${encodeURIComponent(runId)}/verify`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Prüfung konnte nicht gestartet werden');
    }
    return response.data.data;
  },

  /**
   * Get cost estimate for a run (requires authentication)
   */
  async getCost(runId: string): Promise<RunCostEstimate> {
    const response = await apiClient.get<ApiResponse<RunCostEstimate>>(`/runs-cost/${runId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch cost estimate');
    }
    return response.data.data;
  },

  /**
   * NC-1052: assemble a tutor Welcome-Card payload for one participant (operator+).
   * Reveals the participant password (from KeyVault) — call only on explicit action.
   */
  async getWelcome(runId: string, sam: string): Promise<WelcomePayload> {
    const response = await apiClient.get<ApiResponse<WelcomePayload>>(
      `/runs/${encodeURIComponent(runId)}/${encodeURIComponent(sam)}/welcome`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch welcome card');
    }
    return response.data.data;
  },
};

/**
 * Logs API
 */
export const logsApi = {
  /**
   * Get logs with optional filters
   */
  async list(params?: {
    correlationId?: string;
    level?: string;
    scriptKey?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<RunLogEntry>> {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<RunLogEntry>>>('/logs', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch logs');
    }
    return response.data.data;
  },
};

/**
 * Participants API
 */
export const participantsApi = {
  /**
   * Get list of participants with optional search
   */
  async list(search?: string): Promise<Participant[]> {
    const response = await apiClient.get<ApiResponse<Participant[]>>('/participants', {
      params: { search },
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch participants');
    }
    return response.data.data;
  },
};

/**
 * System Status API
 */
export const statusApi = {
  /**
   * Get system health status
   */
  async getStatus(): Promise<SystemStatus> {
    const response = await apiClient.get<ApiResponse<SystemStatus>>('/status');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch system status');
    }
    return response.data.data;
  },
};

/**
 * Dashboard Statistics API
 */
export const statsApi = {
  /**
   * Get dashboard statistics
   */
  async getStats(): Promise<DashboardStats> {
    const response = await apiClient.get<ApiResponse<DashboardStats>>('/stats');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch dashboard statistics');
    }
    return response.data.data;
  },
};

/**
 * Accounts/Password API
 */
export const accountsApi = {
  /**
   * Get account password from KeyVault (requires Operator+ role)
   */
  async getPassword(runId: string, sam: string): Promise<{ password: string; retrievedBy: string; retrievedAt: string }> {
    const response = await apiClient.get<ApiResponse<{ password: string; retrievedBy: string; retrievedAt: string }>>(
      `/accounts/${runId}/${sam}/password`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch password');
    }
    return response.data.data;
  },

  /**
   * Reset account password — generates new password and writes to KeyVault (requires Operator+ role)
   */
  async resetPassword(runId: string, sam: string): Promise<PasswordResetResult> {
    const response = await apiClient.post<ApiResponse<PasswordResetResult>>(
      `/accounts/${runId}/${sam}/password-reset`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to reset password');
    }
    return response.data.data;
  },
};

/**
 * Audit Log API (Admin only)
 */
export const auditApi = {
  async list(params?: {
    userId?: string;
    action?: string;
    from?: string;
    to?: string;
  }): Promise<AuditLogEntry[]> {
    const response = await apiClient.get<ApiResponse<AuditLogEntry[]>>('/audit-log', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch audit log');
    }
    return response.data.data;
  },
};

/**
 * Azure Resources API (Admin only) — NC-540
 *
 * Read-only client for GET /api/azure-resources. The BE wraps Azure
 * Resource Graph and surfaces VMs/Disks/NICs/PublicIPs across all
 * configured workload subscriptions in a single call. Filters are passed
 * through verbatim (the BE escapes for KQL).
 */
export const azureResourcesApi = {
  async list(params?: {
    type?: string;          // comma-separated list, or '*' to lift filter
    subscription?: string;  // comma-separated subscription ids
    tag?: string;           // '<key>=<value>'
    name?: string;          // case-insensitive substring
    skipToken?: string;     // pagination cursor
  }): Promise<AzureResourceListingResponse> {
    const response = await apiClient.get<ApiResponse<AzureResourceListingResponse>>(
      '/azure-resources',
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch Azure resources');
    }
    return response.data.data;
  },

  // NC-543: portal-side metadata (notes/owner/businessContext/linkedRunId/links).
  // The BE 200-response may carry `data: null` when no metadata document
  // exists for the resource — surface that as `null` for the UI.
  async getMetadata(resourceId: string): Promise<ResourceMetadata | null> {
    const response = await apiClient.get<ApiResponse<ResourceMetadata | null>>(
      '/resource-metadata',
      { params: { resourceId } },
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch resource metadata');
    }
    return response.data.data ?? null;
  },

  async upsertMetadata(input: ResourceMetadataInput): Promise<ResourceMetadata> {
    const response = await apiClient.put<ApiResponse<ResourceMetadata>>(
      '/resource-metadata',
      input,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to save resource metadata');
    }
    return response.data.data;
  },
};

/**
 * Drift API (Admin only) — NC-522 Phase 1
 *
 * Read-only client for the drift detector. Scope is the workload
 * subscriptions configured server-side (env var DRIFT_WORKLOADS); the
 * optional `subscription` filter is passed through verbatim.
 */
export const driftApi = {
  async listDrift(params?: { subscription?: string }): Promise<DriftListingResponse> {
    const response = await apiClient.get<ApiResponse<DriftListingResponse>>(
      // NC-937: route MUST NOT start with the `admin/` segment — the Azure
      // Functions host reserves `/admin/*` for its management API, so under
      // SWA Managed Functions `admin/*` routes never register (permanent 404).
      // Served at /api/admin-drift (single segment).
      '/admin-drift',
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch drift');
    }
    return response.data.data;
  },

  async listPersistent(params?: { subscription?: string }): Promise<PersistentResourcesListingResponse> {
    const response = await apiClient.get<ApiResponse<PersistentResourcesListingResponse>>(
      // NC-937: see listDrift above — `admin/` is a reserved Functions-host
      // route segment. Served at /api/admin-persistent-resources.
      '/admin-persistent-resources',
      { params },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch persistent resources');
    }
    return response.data.data;
  },
};

/**
 * Feedback API
 */
export const feedbackApi = {
  async create(feedback: {
    category: string;
    description: string;
    pageUrl: string;
  }): Promise<void> {
    const response = await apiClient.post<ApiResponse<unknown>>('/feedback', feedback);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to submit feedback');
    }
  },
};

/**
 * Release Notes API
 */
export const releaseNotesApi = {
  async list(): Promise<ReleaseNotesData> {
    const response = await apiClient.get<ApiResponse<ReleaseNotesData>>('/release-notes');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch release notes');
    }
    return response.data.data;
  },
};

/**
 * Network API (NC-1579)
 */
export const networkApi = {
  /**
   * The caller's public IPv4 as Azure sees it — for "Meine aktuelle IP
   * übernehmen" next to the RDP whitelist. 422 when no IPv4 is known (IPv6-only
   * connection); the error carries a German hint for the user.
   */
  async getClientIp(): Promise<ClientIpResult> {
    const response = await apiClient.get<ApiResponse<ClientIpResult>>('/auth/client-ip');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'IP-Adresse konnte nicht ermittelt werden');
    }
    return response.data.data;
  },
};

export default apiClient;
